import { Instagram, Youtube, Music2, Mail, Phone, MapPin, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const socialLinks = [
    {
      icon: Instagram,
      href: "https://instagram.com/oerangborneo",
      label: "Instagram",
    },
    {
      icon: Youtube,
      href: "#",
      label: "YouTube",
    },
    {
      icon: Music2,
      href: "#",
      label: "TikTok",
    },
  ];

  const quickLinks = [
    { label: "Home", path: "/" },
    { label: "Referensi", path: "/referensi" },
    { label: "About", path: "/about" },
  ];

  const handleNavClick = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer
      data-testid="footer"
      className="relative bg-[#0A1628] border-t border-white/10 overflow-hidden"
    >
      {/* Top Glow Line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-[#E8761A] to-transparent" />

      {/* Background Glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#E8761A]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <a href="#hero" className="flex items-center gap-3 mb-6 group">
              <img
                src="https://i.ibb.co.com/kgSskVkk/OB-LOGO.png"
                alt="Oerang Borneo Film"
                data-testid="footer-logo"
                className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
              />
              <div>
                <h3 className="text-[#E8761A] font-bold text-lg">
                  OERANG BORNEO FILM
                </h3>
                <p className="text-white/50 text-sm tracking-wider">
                  Visual Storyteller from Borneo
                </p>
              </div>
            </a>
            <p
              data-testid="footer-description"
              className="text-white/60 text-sm leading-relaxed max-w-md mb-6"
            >
              Perusahaan perfilman dan industri kreatif yang bergerak di bidang visual
              storytelling. Menyampaikan gagasan, identitas, dan pesan bermakna melalui
              karya visual.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${social.label.toLowerCase()}`}
                  className="w-10 h-10 bg-white/5 border border-white/10 rounded-full flex items-center justify-center text-white/60 hover:text-[#E8761A] hover:bg-[#E8761A]/10 hover:border-[#E8761A]/30 transition-all duration-300"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              data-testid="footer-quicklinks-title"
              className="text-white font-semibold mb-6"
            >
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => handleNavClick(link.path)}
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                    className="text-white/60 hover:text-[#E8761A] transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4
              data-testid="footer-contact-title"
              className="text-white font-semibold mb-6"
            >
              Hubungi Kami
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="https://wa.me/6289516500862"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="flex items-center gap-3 text-white/60 hover:text-[#E8761A] transition-colors duration-300 text-sm group"
                >
                  <div className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center group-hover:bg-[#E8761A]/10 transition-colors">
                    <Phone className="w-4 h-4" />
                  </div>
                  <span>+62 895 1650 0862</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:oerangborneo@gmail.com"
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-white/60 hover:text-[#E8761A] transition-colors duration-300 text-sm group"
                >
                  <div className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center group-hover:bg-[#E8761A]/10 transition-colors">
                    <Mail className="w-4 h-4" />
                  </div>
                  <span>oerangborneo@gmail.com</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/60 text-sm">
                <div className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <span>Kalimantan, Indonesia</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p
              data-testid="footer-copyright"
              className="text-white/40 text-sm text-center md:text-left"
            >
              © {currentYear} Oerang Borneo Film. All rights reserved.
            </p>
            <p className="text-white/40 text-sm flex items-center gap-1">
              Made with <Heart className="w-4 h-4 text-[#E8761A]" fill="#E8761A" /> in
              Borneo
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
